import { keepPreviousData, queryOptions, type QueryClient } from "@tanstack/react-query";
import {
  fetchPersona,
  fetchPersonaOnboardingStatus,
  fetchPersonaOnboardingSuggestions,
  fetchPersonaUsage,
  fetchPersonas,
} from "@/features/personas/api/personas.api";
import type {
  Persona,
  PersonaListResponse,
  PersonaOnboardingStatus,
  PersonaOnboardingSuggestionsResponse,
  PersonaUsage,
} from "@/features/personas/types/personas.types";

export const personaKeys = {
  all: ["personas"] as const,
  /** List is cached per tenant + includeRejected so the toggle does not collide. */
  list: (tenantId: string, includeRejected = false) =>
    ["personas", "list", tenantId, { includeRejected }] as const,
  /** Prefix for all list variants of a tenant — use with invalidateQueries. */
  lists: (tenantId: string) => ["personas", "list", tenantId] as const,
  details: () => ["personas", "detail"] as const,
  detail: (tenantId: string, id: string) => ["personas", "detail", tenantId, id] as const,
  usage: (tenantId: string, id: string) => ["personas", "usage", tenantId, id] as const,
  onboarding: {
    /** Prefix for all onboarding queries for a tenant — use with invalidateQueries. */
    all: (tenantId: string) => ["personas", "onboarding", tenantId] as const,
    status: (tenantId: string) => ["personas", "onboarding", tenantId, "status"] as const,
    suggestions: (tenantId: string) =>
      ["personas", "onboarding", tenantId, "suggestions"] as const,
  },
} as const;

/**
 * A finished review moves personas out of the suggestion queue and into the
 * library, so the onboarding and list caches always refresh as a pair.
 */
export async function invalidatePersonaOnboarding(
  queryClient: QueryClient,
  tenantId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: personaKeys.onboarding.all(tenantId) }),
    queryClient.invalidateQueries({ queryKey: personaKeys.lists(tenantId) }),
  ]);
}

export function personasQueryOptions(
  tenantId: string | null | undefined,
  options: { includeRejected?: boolean } = {},
) {
  const tid = tenantId || "__none__";
  const includeRejected = options.includeRejected === true;
  return queryOptions({
    queryKey: personaKeys.list(tid, includeRejected),
    queryFn: (): Promise<PersonaListResponse> => fetchPersonas({ includeRejected }),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    // PersonasPage surfaces a persona-specific load-failure toast on `isError`;
    // opt out of the generic global QueryCache.onError toast to avoid a duplicate.
    meta: { skipGlobalError: true },
  });
}

/** Full persona profile for the detail drawer (U5). `id` is empty while the drawer is closed. */
export function personaDetailQueryOptions(tenantId: string | null | undefined, id: string) {
  const tid = tenantId || "__none__";
  return queryOptions({
    queryKey: personaKeys.detail(tid, id),
    queryFn: (): Promise<Persona> => fetchPersona(id),
    enabled: !!tenantId && !!id,
    meta: { skipGlobalError: true },
  });
}

/**
 * Downstream usage for the drawer's "Used in" tab. Gated separately from the
 * profile: the response names opportunity sets, so a caller without
 * `PERSONAS_GET_USAGE` still gets the rest of the drawer.
 */
export function personaUsageQueryOptions(
  tenantId: string | null | undefined,
  id: string,
  options: { enabled?: boolean } = {},
) {
  const tid = tenantId || "__none__";
  return queryOptions({
    queryKey: personaKeys.usage(tid, id),
    queryFn: (): Promise<PersonaUsage> => fetchPersonaUsage(id),
    enabled: !!tenantId && !!id && options.enabled !== false,
    meta: { skipGlobalError: true },
  });
}

/** Cadence for watching a persona sync pass finish — a BQ pass takes minutes, not seconds. */
export const PERSONA_SYNC_POLL_MS = 5000;

export function personaOnboardingStatusQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId || "__none__";
  return queryOptions({
    queryKey: personaKeys.onboarding.status(tid),
    queryFn: (): Promise<PersonaOnboardingStatus> => fetchPersonaOnboardingStatus(),
    enabled: !!tenantId,
    // Polling is a pure function of server state: keep refetching while the tenant's
    // persona sync is mid-pass, stop once it closes. Without this the page holds the
    // analyzing view forever, since `refetchOnWindowFocus` is off app-wide and nothing
    // else invalidates this key while the user waits.
    refetchInterval: (query) =>
      query.state.data?.personasSyncInFlight ? PERSONA_SYNC_POLL_MS : false,
    meta: { skipGlobalError: true },
  });
}

export function personaOnboardingSuggestionsQueryOptions(
  tenantId: string | null | undefined,
  enabled = true,
) {
  const tid = tenantId || "__none__";
  return queryOptions({
    queryKey: personaKeys.onboarding.suggestions(tid),
    queryFn: (): Promise<PersonaOnboardingSuggestionsResponse> =>
      fetchPersonaOnboardingSuggestions(),
    enabled: !!tenantId && enabled,
    // Accept/reject mutate SUGGESTED → ACTIVE/REJECTED; a focus refetch would
    // yank decided cards out of the carousel mid-review.
    refetchOnWindowFocus: false,
    meta: { skipGlobalError: true },
  });
}
